/* Evarra product: variant tiles, subscription toggle, gallery, quantity,
   add to cart (native, theme drawer or AJAX) and sticky add to cart.
   No dependencies. The form works without JS for the preselected variant. */
(() => {
  if (customElements.get('ev-product')) return;

  const formatMoney = (cents, format = '${{amount}}') => {
    const fmt = (n, dec = 2, thou = ',', decSep = '.') => {
      const [i, d] = (n / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? decSep + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: fmt(cents),
      amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
      amount_with_space_separator: fmt(cents, 2, ' ', ',')
    }[key] ?? fmt(cents)));
  };

  class EvProduct extends HTMLElement {
    connectedCallback() {
      const data = JSON.parse(this.querySelector('[data-ev-product-json]').textContent);
      this.variants = data.variants;
      this.moneyFormat = data.moneyFormat;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.idInput = this.form.querySelector('[data-ev-id]');
      this.planInput = this.form.querySelector('[data-ev-plan]');
      this.subToggle = this.querySelector('[data-ev-sub-toggle]');
      this.atc = this.form.querySelector('[data-ev-atc]');
      this.atcLabel = this.form.querySelector('[data-ev-atc-label]');
      this.error = this.form.querySelector('[data-ev-error]');
      this.sticky = this.querySelector('[data-ev-sticky]');

      this.querySelectorAll('[data-ev-variant-radio]').forEach((r) =>
        r.addEventListener('change', () => r.checked && this.setVariant(Number(r.value)))
      );
      this.subToggle?.addEventListener('change', () => this.update());
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.querySelectorAll('[data-ev-qty]').forEach((b) =>
        b.addEventListener('click', () => {
          const input = this.form.querySelector('[data-ev-qty-input]');
          input.value = Math.max(1, Math.min(99, (parseInt(input.value, 10) || 1) + Number(b.dataset.evQty)));
        })
      );

      this.initGallery();
      this.initSticky();
      this.setVariant(Number(this.idInput.value), { silentUrl: true });
    }

    get variant() {
      return this.variants.find((v) => v.id === this.currentId);
    }

    setVariant(id, { silentUrl = false } = {}) {
      this.currentId = id;
      this.idInput.value = id;
      this.querySelectorAll('[data-ev-variant-radio]').forEach((r) => {
        r.checked = Number(r.value) === id;
        r.closest('.ev-tile')?.classList.toggle('is-selected', r.checked);
      });
      if (!silentUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
        if (this.variant?.media) this.showMedia(this.variant.media);
      }
      this.update();
    }

    update() {
      const v = this.variant;
      if (!v) return;
      const planId = this.subToggle?.dataset.planId;
      const planPrice = planId && v.plans[planId] !== undefined ? v.plans[planId] : null;
      const subscribed = Boolean(this.subToggle?.checked && planPrice !== null);

      if (this.planInput) this.planInput.disabled = !subscribed;
      if (this.subToggle) {
        const subPriceEl = this.querySelector('[data-ev-sub-price]');
        if (subPriceEl) {
          subPriceEl.hidden = planPrice === null;
          if (planPrice !== null) subPriceEl.textContent = `${formatMoney(planPrice, this.moneyFormat)} per delivery`;
        }
      }

      const price = subscribed ? planPrice : v.price;
      const label = this.atcLabel;
      this.atc.disabled = !v.available;
      label.textContent = v.available
        ? label.dataset.labelAdd + (label.dataset.showPrice === 'true' ? ` · ${formatMoney(price, this.moneyFormat)}` : '')
        : label.dataset.labelSoldout;

      const priceEl = this.querySelector('[data-ev-price]');
      if (priceEl) priceEl.textContent = formatMoney(price, this.moneyFormat);
      const variantEl = this.querySelector('[data-ev-sticky-variant]');
      if (variantEl && this.variants.length > 1) variantEl.textContent = `${v.title} · `;
      const stickyBtn = this.querySelector('[data-ev-sticky-btn]');
      if (stickyBtn && this.stickyMode !== 'choose') stickyBtn.disabled = !v.available;
      if (this.error) this.error.hidden = true;
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      this.viewport = this.querySelector('[data-ev-viewport]');
      this.thumbs = [...this.querySelectorAll('[data-ev-thumb]')];
      if (!this.viewport) return;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.scrollToSlide(Number(t.dataset.index))));
      let ticking = false;
      this.viewport.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
          this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
          ticking = false;
        });
      }, { passive: true });
    }

    scrollToSlide(index) {
      this.viewport.scrollTo({ left: index * this.viewport.clientWidth, behavior: 'smooth' });
    }

    showMedia(mediaId) {
      const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (slide) this.scrollToSlide(Number(slide.dataset.index));
    }

    /* ---------- Sticky ATC ---------- */
    initSticky() {
      if (!this.sticky) return;
      const btn = this.sticky.querySelector('[data-ev-sticky-btn]');
      const tiers = btn && document.getElementById(btn.dataset.target);
      const early = this.sticky.dataset.early === 'true';
      const inView = (r) => r.bottom > 0 && r.top < window.innerHeight;
      let ticking = false;
      const apply = () => {
        ticking = false;
        const atc = this.atc.getBoundingClientRect();
        const afterAtc = atc.bottom < 0;
        const beforeBuy = early && atc.top > window.innerHeight && window.scrollY > 300 && !(tiers && inView(tiers.getBoundingClientRect()));
        const show = afterAtc || beforeBuy;
        this.stickyMode = afterAtc || !tiers ? 'add' : 'choose';
        if (btn) {
          const label = this.stickyMode === 'add' ? btn.dataset.labelAdd : btn.dataset.labelChoose;
          if (btn.textContent !== label) btn.textContent = label;
          btn.disabled = this.stickyMode === 'add' && !this.variant?.available;
        }
        if (this.sticky.classList.contains('is-visible') !== show) {
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', String(!show));
          this.sticky.inert = !show;
        }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      apply();
      btn?.addEventListener('click', (e) => {
        if (this.stickyMode === 'choose' && tiers) {
          e.preventDefault();
          tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
          tiers.querySelector('input:checked')?.focus({ preventScroll: true });
        }
      });
    }

    /* ---------- Add to cart ---------- */
    themeCart() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    onSubmit(event) {
      const mode = this.dataset.cartMode;
      const cart = this.themeCart();
      if (mode === 'redirect' || (mode === 'auto' && !(cart && typeof cart.renderContents === 'function'))) {
        return; // native POST to /cart/add then /cart: works with every theme and discount
      }
      event.preventDefault();
      if (this.atc.classList.contains('is-loading')) return;
      this.addToCart(cart);
    }

    async addToCart(cart) {
      const buttons = [this.atc, this.querySelector('[data-ev-sticky-btn]')].filter(Boolean);
      buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); });
      const body = new FormData(this.form);
      const canRender = cart && typeof cart.renderContents === 'function';
      if (canRender && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart.');

        document.dispatchEvent(new CustomEvent('evarra:cart:added', { detail: json, bubbles: true }));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'evarra-product', productVariantId: this.currentId, cartData: json });
        }
        if (canRender) {
          cart.classList.remove('is-empty');
          cart.renderContents(json);
        } else if (this.dataset.cartMode !== 'ajax') {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        } else {
          const label = this.atcLabel;
          const previous = label.textContent;
          label.textContent = 'Added to cart ✓';
          setTimeout(() => { label.textContent = previous; }, 2000);
        }
      } catch (err) {
        if (this.error) {
          this.error.textContent = err.message;
          this.error.hidden = false;
        }
      } finally {
        buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
      }
    }
  }

  customElements.define('ev-product', EvProduct);
  window.EvarraFormatMoney = formatMoney;
})();
